import { useCallback } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useCreateThread } from "@/api/queries";
import { usePreferredSource } from "@/lib/preferredSource";

/**
 * Starting a conversation, from wherever the button happens to be.
 *
 * The source it reads is the shared working context (`usePreferredSource`),
 * not a copy of its own: the sidebar starts conversations, the chat page's
 * empty state starts conversations, and the workbench answers questions about
 * the same source. One remembered answer, three surfaces.
 */

export function useNewConversation() {
  const { sources, connectionId, select: remember } = usePreferredSource();
  const createThread = useCreateThread();
  const navigate = useNavigate();

  const start = useCallback(
    async (id: string) => {
      if (!id) return;
      remember(id);
      const created = await createThread
        .mutateAsync({
          connection_id: id,
          title: "",
          // Always a new conversation. The blank it replaces is discarded
          // server-side, so blanks still never stack (PRD FR-4.8).
          replace_empty: true,
          model_ref: null,
        })
        // Caught, not swallowed: the mutation cache has already said why
        // (backlog item 11), and all this needs to do is not navigate to a
        // conversation that was never created — and not leave an unhandled
        // rejection behind while it does nothing.
        .catch(() => null);
      if (!created) return;
      await navigate({ to: "/chat/$threadId", params: { threadId: created.id } });
    },
    [createThread, navigate, remember],
  );

  return { sources, connectionId, select: remember, start, creating: createThread.isPending };
}
